import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon, LinkIcon, PhotoIcon } from '@heroicons/react/24/outline';
import ConfirmDialog from '../components/ConfirmDialog';

const AboutUsJourneyPage = () => {
    const [videos, setVideos] = useState([]);
    const [settings, setSettings] = useState({
        section_title: 'Watch Our Journey',
        section_description: 'See how we help families build their dream homes.',
        is_active: true
    });
    const [loading, setLoading] = useState(true);
    const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
    const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
    const [currentVideo, setCurrentVideo] = useState(null);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    // Fetch Data
    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            setLoading(true);
            const [videosRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/about-aham/videos'),
                axios.get('/api/admin/about-aham/videos/settings')
            ]);

            if (videosRes.data.success) {
                setVideos(videosRes.data.data);
            }
            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    // Settings Handler
    const handleSettingsSubmit = async (e) => {
        e.preventDefault();
        try {
            await axios.put('/api/admin/about-aham/videos/settings', settings);
            toast.success('Settings updated successfully');
            setIsSettingsModalOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error updating settings:', error);
            toast.error('Failed to update settings');
        }
    };

    // Video Handlers
    const handleVideoSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData(e.target);

        // Handle checkbox manually
        if (!formData.get('is_active')) {
            formData.append('is_active', false);
        } else {
            formData.set('is_active', true);
        }

        try {
            if (currentVideo) {
                await axios.put(`/api/admin/about-aham/videos/${currentVideo.id}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Video updated successfully');
            } else {
                await axios.post('/api/admin/about-aham/videos', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                toast.success('Video added successfully');
            }
            setIsVideoModalOpen(false);
            setCurrentVideo(null);
            fetchData();
        } catch (error) {
            console.error('Error saving video:', error);
            toast.error('Failed to save video');
        }
    };

    const handleDelete = async () => {
        try {
            await axios.delete(`/api/admin/about-aham/videos/${deleteId}`);
            toast.success('Video deleted successfully');
            setIsDeleteOpen(false);
            fetchData();
        } catch (error) {
            console.error('Error deleting video:', error);
            toast.error('Failed to delete video');
        }
    };

    return (
        <div className="p-6 space-y-6">
            <div className="flex justify-between items-center">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Watch Our Journey</h1>
                    <p className="text-gray-500">Manage videos and section settings</p>
                </div>
                <div className="flex gap-3">
                    <button
                        onClick={() => setIsSettingsModalOpen(true)}
                        className="btn btn-secondary"
                    >
                        Edit Settings
                    </button>
                    <button
                        onClick={() => {
                            setCurrentVideo(null);
                            setIsVideoModalOpen(true);
                        }}
                        className="btn btn-primary flex items-center gap-2"
                    >
                        <PlusIcon className="w-5 h-5" />
                        Add Video
                    </button>
                </div>
            </div>

            {/* List */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {videos.map((video) => (
                    <div key={video.id} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden group">
                        <div className="relative aspect-video bg-gray-100">
                            <img
                                src={video.thumbnail_url?.startsWith('http') ? video.thumbnail_url : `/${video.thumbnail_url}`}
                                alt={video.title}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <button
                                    onClick={() => {
                                        setCurrentVideo(video);
                                        setIsVideoModalOpen(true);
                                    }}
                                    className="p-2 bg-white rounded-full text-blue-600 hover:bg-blue-50"
                                >
                                    <PencilIcon className="w-5 h-5" />
                                </button>
                                <button
                                    onClick={() => {
                                        setDeleteId(video.id);
                                        setIsDeleteOpen(true);
                                    }}
                                    className="p-2 bg-white rounded-full text-red-600 hover:bg-red-50"
                                >
                                    <TrashIcon className="w-5 h-5" />
                                </button>
                            </div>
                            <div className="absolute top-2 right-2 px-2 py-1 rounded text-xs font-medium bg-white/90">
                                #{video.display_order}
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="font-semibold text-gray-900 line-clamp-1">{video.title}</h3>
                            <a href={video.video_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline flex items-center gap-1 mt-1">
                                <LinkIcon className="w-4 h-4" />
                                Watch Video
                            </a>
                        </div>
                    </div>
                ))}
            </div>

            {/* Settings Modal */}
            {isSettingsModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                        <h2 className="text-xl font-bold mb-4">Section Settings</h2>
                        <form onSubmit={handleSettingsSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Section Title</label>
                                <input
                                    type="text"
                                    value={settings.section_title}
                                    onChange={(e) => setSettings({ ...settings, section_title: e.target.value })}
                                    className="input-field mt-1 w-full"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Description</label>
                                <textarea
                                    value={settings.section_description}
                                    onChange={(e) => setSettings({ ...settings, section_description: e.target.value })}
                                    className="input-field mt-1 w-full"
                                    rows="3"
                                />
                            </div>
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    checked={settings.is_active}
                                    onChange={(e) => setSettings({ ...settings, is_active: e.target.checked })}
                                    className="rounded border-gray-300 text-blue-600"
                                />
                                <span className="text-sm text-gray-700">Section Active</span>
                            </div>
                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setIsSettingsModalOpen(false)} className="btn btn-secondary">Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Changes</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Video Modal */}
            {isVideoModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
                    <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6">
                        <h2 className="text-xl font-bold mb-4">{currentVideo ? 'Edit Video' : 'Add New Video'}</h2>
                        <form onSubmit={handleVideoSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Title</label>
                                <input
                                    name="title"
                                    defaultValue={currentVideo?.title}
                                    className="input-field mt-1 w-full"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Video URL (YouTube/Vimeo)</label>
                                <div className="flex gap-2">
                                    <span className="flex items-center text-gray-400"><LinkIcon className="w-5 h-5" /></span>
                                    <input
                                        name="video_url"
                                        defaultValue={currentVideo?.video_url}
                                        className="input-field mt-1 w-full"
                                        placeholder="https://youtube.com/..."
                                        required
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700">Thumbnail Image</label>
                                    <p className="text-xs text-gray-500 mb-1">Recommended: 1280 x 720 pixels </p>
                                <p className="text-xs text-gray-500 mb-2">Formats: WebP, PNG, JPEG | Max 2MB</p>
                                <div className="flex items-center gap-4">
                                    {(currentVideo?.thumbnail_url) && (
                                        <img
                                            src={currentVideo.thumbnail_url.startsWith('http') ? currentVideo.thumbnail_url : `/${currentVideo.thumbnail_url}`}
                                            className="w-16 h-16 object-cover rounded border"
                                        />
                                    )}
                                    <input
                                        type="file"
                                        name="thumbnail"
                                        accept="image/webp, image/png, image/jpeg"
                                        onChange={(e) => {
                                            const file = e.target.files[0];
                                            if (file) {
                                                const allowedTypes = ['image/webp', 'image/png', 'image/jpeg'];
                                                if (!allowedTypes.includes(file.type)) {
                                                    toast.error('Only WebP, JPEG and PNG formats are allowed');
                                                    e.target.value = '';
                                                    return;
                                                }
                                                if (file.size > 2 * 1024 * 1024) {
                                                    toast.error('File size must be less than 2MB');
                                                    e.target.value = '';
                                                    return;
                                                }
                                            }
                                        }}
                                        className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                    />
                                </div>
                                <div className="mt-2">
                                    <label className="text-xs text-gray-600 block">Or use External Thumbnail URL (if not uploading)</label>
                                    <input
                                        name="thumbnail_url"
                                        defaultValue={currentVideo?.thumbnail_url}
                                        placeholder="https://img.youtube.com/..."
                                        className="input-field mt-1 w-full text-sm"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Display Order</label>
                                    <input
                                        type="number"
                                        name="display_order"
                                        defaultValue={currentVideo?.display_order || 0}
                                        className="input-field mt-1 w-full"
                                    />
                                </div>
                                <div className="flex items-end mb-2">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            name="is_active"
                                            defaultChecked={currentVideo?.is_active ?? true}
                                            className="rounded border-gray-300 text-blue-600"
                                        />
                                        <span className="text-sm text-gray-700">Active</span>
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 mt-6">
                                <button type="button" onClick={() => setIsVideoModalOpen(false)} className="btn btn-secondary">Cancel</button>
                                <button type="submit" className="btn btn-primary">Save Video</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <ConfirmDialog
                isOpen={isDeleteOpen}
                onClose={() => setIsDeleteOpen(false)}
                onConfirm={handleDelete}
                title="Delete Video"
                message="Are you sure you want to delete this video? This action cannot be undone."
            />
        </div>
    );
};

export default AboutUsJourneyPage;
